import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n";
import { getProductsPage, getCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ProductSkeleton } from "../components/Loaders";
import ObiCategoryBanner from "../components/ObiCategoryBanner";
import Pagination from "../components/Pagination";
import { OBI } from "../assets/obi";

const SORT_OPTIONS = [
  { value: "date|desc", labelKey: "catalog.sort_new" },
  { value: "price|asc", labelKey: "catalog.sort_price_asc" },
  { value: "price|desc", labelKey: "catalog.sort_price_desc" },
  { value: "popularity|desc", labelKey: "catalog.sort_popular" },
];

const CatalogPage = () => {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearch = searchParams.get("search") || "";
  const initialCategory = searchParams.get("category") ? Number(searchParams.get("category")) : null;
  const initialPage = Number(searchParams.get("page") || 1);

  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState("date|desc");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [perPage] = useState(24);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load categories once
  useEffect(() => {
    getCategories()
      .then((data) => setCategories(data.filter((c) => c.slug !== "uncategorized")))
      .catch(() => {});
  }, []);

  // Re-fetch when filters change
  useEffect(() => {
    const [orderby, order] = sort.split("|");
    setLoading(true);
    const params = { per_page: perPage, page, orderby, order };
    if (search) params.search = search;
    if (category) params.category = category;
    if (priceRange.min) params.min_price = priceRange.min;
    if (priceRange.max) params.max_price = priceRange.max;

    // sync URL
    const next = new URLSearchParams();
    if (search) next.set("search", search);
    if (category) next.set("category", String(category));
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });

    getProductsPage(params)
      .then((data) => {
        setProducts(data.items || []);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      })
      .catch((e) => {
        console.error(e);
        setProducts([]);
        setTotalPages(1);
        setTotal(0);
      })
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, sort, priceRange.min, priceRange.max, page, perPage]);

  // Reset to page 1 when filters change (but not when only page changes)
  useEffect(() => {
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, sort, priceRange.min, priceRange.max]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const resetFilters = () => {
    setSearch("");
    setSearchInput("");
    setCategory(null);
    setSort("date|desc");
    setPriceRange({ min: "", max: "" });
  };

  const FiltersPanel = (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Search className="w-4 h-4" /> Cerca
        </h3>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            data-testid="catalog-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("nav.search_placeholder")}
            className="cartoon-input flex-1 py-2 text-sm"
          />
          <button type="submit" className="cartoon-btn bg-sky-300 py-2 px-3 text-sm">
            <Search className="w-4 h-4" />
          </button>
        </form>
      </div>

      <div>
        <h3 className="font-display font-bold text-lg mb-3">{t("catalog.categories")}</h3>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
            <input
              type="radio"
              name="cat"
              checked={!category}
              onChange={() => setCategory(null)}
              className="accent-sky-500 w-4 h-4"
              data-testid="filter-cat-all"
            />
            Tutte
          </label>
          {categories.map((c) => (
            <label key={c.id} className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="radio"
                name="cat"
                checked={category === c.id}
                onChange={() => setCategory(c.id)}
                className="accent-sky-500 w-4 h-4"
                data-testid={`filter-cat-${c.id}`}
              />
              {c.name} <span className="text-xs text-slate-400">({c.count})</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-lg mb-3">{t("catalog.price_range")}</h3>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            placeholder="Min"
            value={priceRange.min}
            onChange={(e) => setPriceRange((p) => ({ ...p, min: e.target.value }))}
            className="cartoon-input w-full py-2 text-sm"
            data-testid="filter-price-min"
          />
          <span className="font-bold">—</span>
          <input
            type="number"
            min={0}
            placeholder="Max"
            value={priceRange.max}
            onChange={(e) => setPriceRange((p) => ({ ...p, max: e.target.value }))}
            className="cartoon-input w-full py-2 text-sm"
            data-testid="filter-price-max"
          />
        </div>
      </div>

      <button
        onClick={resetFilters}
        data-testid="reset-filters-button"
        className="cartoon-btn bg-yellow-200 hover:bg-yellow-100 w-full py-2 text-sm"
      >
        {t("catalog.reset_filters")}
      </button>
    </div>
  );

  return (
    <div data-testid="catalog-page" className="min-h-screen">
      {/* HEADER */}
      <section className="bg-pink-200 border-b-4 border-slate-900 py-10 bg-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
          <div>
            <h1 className="font-display font-bold text-4xl lg:text-5xl text-slate-900">
              {t("catalog.title")}
            </h1>
            <p className="text-slate-700 mt-2">
              {!loading && `${total} ${t("catalog.results_count")}`}
            </p>
          </div>
          <img src={OBI.dice} alt="OBI" className="hidden md:block w-32 h-32 object-contain" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <button
            data-testid="open-filters-button"
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden cartoon-btn bg-white hover:bg-yellow-200 py-2 px-4 text-sm"
          >
            <SlidersHorizontal className="w-4 h-4" /> {t("catalog.filters")}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-sm font-bold text-slate-700 hidden sm:block">{t("catalog.sort")}:</label>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="cartoon-input py-2 text-sm"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="cartoon-card p-6 sticky top-28">{FiltersPanel}</div>
          </aside>

          <main className="lg:col-span-9">
            <ObiCategoryBanner categoryId={category} />
            {loading ? (
              <ProductSkeleton count={8} />
            ) : products.length === 0 ? (
              <div className="cartoon-card p-12 text-center" data-testid="no-results">
                <img src={OBI.empty} alt="OBI" className="w-44 h-44 mx-auto" />
                <h3 className="font-display font-bold text-2xl mt-4">{t("catalog.no_results")}</h3>
                <p className="text-slate-600 mt-2">{t("catalog.no_results_sub")}</p>
                <button onClick={resetFilters} className="cartoon-btn bg-yellow-300 mt-6">
                  {t("catalog.reset_filters")}
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {products.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
                </div>
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onChange={setPage}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </section>

      {/* Mobile filters drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="filter-drawer">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-full max-w-sm bg-amber-50 border-l-4 border-slate-900 overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b-4 border-slate-900 bg-pink-300">
              <h2 className="font-display font-bold text-2xl">{t("catalog.filters")}</h2>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-10 h-10 bg-white border-4 border-slate-900 rounded-full flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">{FiltersPanel}</div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CatalogPage;
